/**
 * API 설정 및 유틸리티 함수
 * 개발 환경과 프로덕션 환경을 자동으로 감지하여 적절한 API URL을 반환
 */

// 환경 감지 함수
export const isDevelopment = () => {
  return window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.port === '3001' ||
         window.location.port === '3000';
};

// API 베이스 URL 설정
export const getApiBaseUrl = () => {
  if (isDevelopment()) {
    return 'http://localhost:8788';
  }
  return ''; // 프로덕션에서는 상대 경로 사용
};

// API 엔드포인트 URL 생성
export const getApiUrl = (endpoint) => {
  const baseUrl = getApiBaseUrl();
  if (!endpoint) return baseUrl;
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
};

// API 요청 헬퍼 함수
export const apiRequest = async (endpoint, options = {}) => {
  const url = getApiUrl(endpoint);
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  console.log(`🔗 API 요청: ${url}`, finalOptions);

  try {
    const response = await fetch(url, finalOptions);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`❌ API 요청 실패 (${url}):`, error);
    throw error;
  }
};

// 특정 API 엔드포인트들
export const API_ENDPOINTS = {
  AUTH_LOGIN: '/api/auth/login',
  AUTH_SIGNUP: '/api/auth/signup',
  AUTH_LOGOUT: '/api/auth/logout',
  YOUTUBE_SUMMARY: '/api/youtube-summary',
  YOUTUBE_SEARCH: '/api/admin/YoutubeSearchApi',
  ADMIN_QUERY: '/api/admin/query',
  ADMIN_POSTS: '/api/admin/posts',
  ADMIN_POSTS_PENDING: '/api/admin/posts/pending',
  ADMIN_POSTS_CONFIRMED: '/api/admin/posts/confirmed',
  ADMIN_POSTS_ALL: '/api/admin/posts/all',
  ADMIN_POSTS_CONFIRM: '/api/admin/posts/confirm',
  ADMIN_SETTINGS: '/api/admin/settings',
};

// Auth API 전용 함수
export const authApi = {
  async login(email, password) {
    return await apiRequest(API_ENDPOINTS.AUTH_LOGIN, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  async signup(email, password, username) {
    return await apiRequest(API_ENDPOINTS.AUTH_SIGNUP, {
      method: 'POST',
      body: JSON.stringify({ email, password, user_name: username }),
    });
  },

  async logout() {
    return await apiRequest(API_ENDPOINTS.AUTH_LOGOUT, {
      method: 'POST',
    });
  },
};

// Admin Query API 전용 함수
export const adminQueryApi = {
  async executeQuery(query, adminKey = 'persona-admin-2024') {
    return await apiRequest(API_ENDPOINTS.ADMIN_QUERY, {
      method: 'POST',
      body: JSON.stringify({ query, adminKey }),
    });
  },
};

// Admin Posts API 전용 함수
export const adminPostsApi = {
  async getAllPosts() {
    return await apiRequest(`${API_ENDPOINTS.ADMIN_POSTS}/all`, {
      method: 'GET',
    });
  },
  
  async getPendingPosts() {
    return await apiRequest(`${API_ENDPOINTS.ADMIN_POSTS}/pending`, {
      method: 'GET',
    });
  },
  
  async confirmPost(postId) {
    return await apiRequest(`${API_ENDPOINTS.ADMIN_POSTS}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ postId }),
    });
  },
};

// YouTube Search API 전용 함수
export const youtubeSearchApi = {
  async searchVideos(searchTerm, maxResults = 10, publishedAfter = null) {
    const params = new URLSearchParams({
      q: searchTerm,
      maxResults: maxResults.toString(),
    });
    
    if (publishedAfter) {
      params.append('publishedAfter', publishedAfter);
    }
    
    return await apiRequest(`${API_ENDPOINTS.YOUTUBE_SEARCH}?${params}`, {
      method: 'GET',
    });
  },

};

// YouTube Summary API 전용 함수
export const youtubeSummaryApi = {
  async generateSummary(videoId, url) {
    return await apiRequest(API_ENDPOINTS.YOUTUBE_SUMMARY, {
      method: 'POST',
      body: JSON.stringify({ videoId, url })
    });
  },

  // YouTube 요약을 post 테이블에 저장
  async saveYoutubeSummary(data) {
    return await apiRequest('/api/youtube-summary/save', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
};

// 환경 정보 출력 (개발 시에만)
if (isDevelopment()) {
  console.log('🚀 개발 환경 감지됨');
  console.log(`📍 API 베이스 URL: ${getApiBaseUrl()}`);
  console.log(`🌐 현재 호스트: ${window.location.hostname}:${window.location.port}`);
}